import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/react-hooks';
import Badge from 'antd/lib/badge';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown';
import Icon from 'antd/lib/icon';
import Input from 'antd/lib/input';
import Menu from 'antd/lib/menu';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import get from 'lodash/get';

import {
  CreateModal,
  Loader,
  showErrorNotification,
  showInfoNotification,
} from 'components';
import { fetchDatasets, datasetDestroy } from 'graphql/queries';
import { useModal, createLink } from 'utils';
import formatRelativeDate from 'utils/formatRelativeDate';

import {
  ActionsRow,
  ActionsColumn,
  DatasetDescription,
  DatasetHeader,
  DatasetContent,
  DatasetItem,
  DatasetList,
  DatasetMeta,
  DatasetName,
  DatasetPage,
  HeaderActions,
  LastUpdatedText,
  SearchInput,
  SortSelect,
  Title,
  Toolbar,
  ZeroState,
  ZeroStateActions,
  ZeroStateDescription,
  ZeroStateTitle,
} from './style';

const { confirm } = Modal;
const { Option } = Select;
const locationBadgeStyle = {
  backgroundColor: '#f5f5f5',
  boxShadow: 'none',
  color: '#595959',
};

const sortDatasets = (datasets, sortBy) => {
  const sorted = [...datasets];

  if (sortBy === 'NAME') {
    sorted.sort((left, right) => left.name.localeCompare(right.name));
    return sorted;
  }

  sorted.sort((left, right) => (
    Date.parse(right.updatedAt || '') - Date.parse(left.updatedAt || '')
  ));
  return sorted;
};

const Datasets = () => {
  const { openModal, ...modalProps } = useModal();
  const [searchValue, setSearchValue] = useState('');
  const [sortBy, setSortBy] = useState('LAST_UPDATED');
  const { error, loading, data } = useQuery(fetchDatasets);

  const [destroyDataset] = useMutation(datasetDestroy, {
    refetchQueries: [{ query: fetchDatasets }],
    onCompleted: (response) => {
      const deletedId = get(response, 'datasetDestroy.deletedId', null);

      if (deletedId) {
        showInfoNotification(`Deleted dataset with id ${deletedId}`);
      }
    },
    onError: (mutationError) => {
      const deletedError = get(mutationError, 'graphQLErrors[0].message', null);

      if (deletedError) {
        showErrorNotification(deletedError);
      }
    },
  });

  const datasets = get(data, 'datasets', []);

  const visibleDatasets = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    const filtered = normalizedSearch.length === 0
      ? datasets
      : datasets.filter(({ name, description }) => (
        `${name || ''} ${description || ''}`.toLowerCase().includes(normalizedSearch)
      ));

    return sortDatasets(filtered, sortBy);
  }, [datasets, searchValue, sortBy]);
  const hasSearchQuery = searchValue.trim().length > 0;
  const hasNoDatasets = datasets.length === 0;
  const hasNoVisibleDatasets = visibleDatasets.length === 0;

  const handleDelete = (dataset) => {
    confirm({
      title: `Are you sure you want to delete ${dataset.name}?`,
      okButtonProps: { id: 'confirm-card-delete' },
      onOk() {
        destroyDataset({
          variables: {
            input: {
              id: dataset.id,
            },
          },
        });
      },
    });
  };

  return (
    <Loader loading={loading} error={error} variant="cardList">
      <DatasetPage>
        <DatasetHeader>
          <div>
            <Title>Datasets</Title>
          </div>
          <HeaderActions>
            <Button
              id="open-main-page-create-modal"
              size="large"
              type="primary"
              onClick={openModal}
            >
              New Dataset
            </Button>
          </HeaderActions>
        </DatasetHeader>
        <Toolbar>
          <SearchInput>
            <Input
              allowClear
              prefix={<Icon type="search" />}
              placeholder="Search datasets..."
              value={searchValue}
              onChange={event => setSearchValue(event.target.value)}
            />
          </SearchInput>
          <SortSelect
            value={sortBy}
            onChange={setSortBy}
            suffixIcon={<Icon type="down" />}
          >
            <Option value="LAST_UPDATED">Sort by: Last Updated</Option>
            <Option value="NAME">Sort by: Name</Option>
          </SortSelect>
        </Toolbar>
        <DatasetList>
          {hasNoVisibleDatasets && (
            <ZeroState>
              <ZeroStateTitle>
                {hasNoDatasets ? 'No datasets yet' : 'No matching datasets'}
              </ZeroStateTitle>
              <ZeroStateDescription>
                {hasNoDatasets
                  ? 'Create your first dataset to get started.'
                  : 'Try a different search or clear your current filter.'}
              </ZeroStateDescription>
              <ZeroStateActions>
                {hasNoDatasets ? (
                  <Button
                    id="open-main-page-create-modal-empty"
                    type="primary"
                    onClick={openModal}
                  >
                    New Dataset
                  </Button>
                ) : (
                  hasSearchQuery && (
                    <Button onClick={() => setSearchValue('')}>
                      Clear Search
                    </Button>
                  )
                )}
              </ZeroStateActions>
            </ZeroState>
          )}
          {visibleDatasets.map((dataset) => {
            const locationsCount = get(dataset, 'datapoints.totalCount', 0);
            const formattedLocationsCount = Number(locationsCount).toLocaleString();
            const actionsMenu = (
              <Menu>
                <Menu.Item key={`open-${dataset.id}`}>
                  <Link to={createLink('edit', dataset.id)}>Open</Link>
                </Menu.Item>
                <Menu.Item
                  key={`delete-${dataset.id}`}
                  onClick={() => handleDelete(dataset)}
                >
                  Delete
                </Menu.Item>
              </Menu>
            );

            return (
              <DatasetItem key={dataset.id}>
                <DatasetContent>
                  <DatasetName to={createLink('edit', dataset.id)}>
                    {dataset.name}
                  </DatasetName>
                  {dataset.description && (
                    <DatasetDescription>{dataset.description}</DatasetDescription>
                  )}
                  <DatasetMeta>
                    <Badge
                      count={`${formattedLocationsCount} ${locationsCount === 1 ? 'Location' : 'Locations'}`}
                      style={locationBadgeStyle}
                    />
                  </DatasetMeta>
                </DatasetContent>
                <ActionsColumn>
                  <ActionsRow>
                    <LastUpdatedText>
                      {`Updated ${formatRelativeDate(dataset.updatedAt)}`}
                    </LastUpdatedText>
                    <Dropdown overlay={actionsMenu} trigger={['click']} placement="bottomRight">
                      <Button icon="ellipsis" />
                    </Dropdown>
                  </ActionsRow>
                </ActionsColumn>
              </DatasetItem>
            );
          })}
        </DatasetList>
        <CreateModal type="Dataset" {...modalProps} />
      </DatasetPage>
    </Loader>
  );
};

export default Datasets;
